import { XCircle } from '@phosphor-icons/react';
import { InputText as PRInputText } from 'primereact/inputtext';
import { InputTextarea as PRInputTextarea } from 'primereact/inputtextarea';
import { useRef, useState, useEffect } from 'react';
import { twJoin, twMerge } from 'tailwind-merge';

export default function Input({
  leftElem,
  rightElem,
  showClearInput,
  value,
  className,
  onKeyDown,
  label,
  id,
  type = 'text',
  disabled = false,
  minRows = 1,
  maxRows = 3,
  dynamicWidth,
  dynamicMinWidth = '10px',
  dynamicMaxWidth = '400px',
  required = false,
  onChange = () => {},
  onFocusChange = () => {},
  ...props
}) {
  const [focusInput, setFocusInput] = useState(false);
  const [textAreaRows, setTextAreaRows] = useState(minRows);
  const divRef = useRef(null);
  const inputRef = useRef(null);
  const handleInputResize = (text) => {
    if (text?.length > 0) {
      const canvas = document.createElement('canvas');
      const context = canvas.getContext('2d');
      const width = context.measureText(text).width;
      const finalWidth = Math.ceil(width * 1.6) + 'px';
      inputRef.current.style.width = `${finalWidth}`;
    } else {
      inputRef.current.style.width = `10px`;
    }
  };
  useEffect(() => {
    if (!focusInput && !value) {
      onFocusChange();
    }
  }, [focusInput]);

  const onTextChange = (event) => {
    onChange(event);
    if (inputRef.current && dynamicWidth) {
      handleInputResize(event.target.value);
    }
  };

  const keyDownHandler = (e) => {
    if (e.key === 'Enter' && inputRef.current) {
      inputRef.current.blur();
    }
    onKeyDown && onKeyDown(e);
  };

  useEffect(() => {
    if (dynamicWidth) handleInputResize(value);
  }, [value]);

  function handleRowResize(event) {
    const textareaDefaultHeight = 20;

    event.target.rows = minRows; // reset to <textAreaRows> to calculate new height

    const textareaCurrentHeight = event.target.scrollHeight;

    const currentRows = textareaCurrentHeight / textareaDefaultHeight;

    if (currentRows > maxRows) {
      event.target.rows = maxRows;
      event.target.style.overflowY = 'scroll';
    } else {
      event.target.rows = currentRows;
      event.target.style.overflowY = 'hidden';
    }

    setTextAreaRows(currentRows < maxRows ? currentRows : maxRows);
  }

  function clearInput(event) {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    event.target.value = '';
    event.target.rows = 1;
    setTextAreaRows(minRows);
    onChange(event);
  }

  useEffect(() => {
    function setInputFocus(e) {
      setFocusInput(true);
    }

    function removeInputFocus() {
      setFocusInput(false);
    }

    if (divRef.current) {
      divRef.current.addEventListener('focusin', setInputFocus);
      divRef.current.addEventListener('focusout', removeInputFocus);
    }

    return () => {
      if (divRef.current) {
        divRef.current.removeEventListener('focusin', setInputFocus);
        divRef.current.removeEventListener('focusout', removeInputFocus);
      }
    };
  }, []);

  return (
    <div
      className={twMerge(
        'flex flex-col gap-1.5',
        twJoin(className?.wrapper && className.wrapper)
      )}
    >
      {label ? (
        <label
          htmlFor={id}
          className={twMerge(
            'text-brand-ai-1100 text-sm',
            twJoin(className?.label && className.label)
          )}
        >
          {label}
          {required ? <span className="text-brand-ai-100">*</span> : null}
        </label>
      ) : null}
      <div
        ref={divRef}
        className={twMerge(
          'flex flex-1 items-center border border-pv-neutral-grey-200 rounded-lg py-3 px-4 bg-white focus-within:border-brand-ai-100',
          twJoin(
            focusInput && 'border-brand-ai-100',
            !disabled && !focusInput && 'hover:border-pv-primary-primary-300',
            disabled && 'bg-pv-neutral-grey-50',
            className?.input?.wrapper && className.input.wrapper
          )
        )}
      >
        {leftElem && <span className="mr-1.5">{leftElem}</span>}
        {type !== 'textarea' ? (
          <PRInputText
            pt={{
              root: {
                className: twMerge(
                  'shadow-none rounded-none w-full resize-none outline-none text-sm text-brand-ai-1100 placeholder:text-pv-text-secondary-text bg-transparent',
                  twJoin(className?.input?.root && className.input.root)
                ),
                style: {
                  ...(dynamicWidth && {
                    minWidth: dynamicMinWidth,
                    maxWidth: dynamicMaxWidth,
                  }),
                },
              },
            }}
            {...(id && { id })}
            {...(typeof value !== 'undefined' && { value })}
            ref={inputRef}
            onChange={onTextChange}
            disabled={disabled}
            onKeyDown={keyDownHandler}
            {...props}
          />
        ) : (
          <PRInputTextarea
            rows={textAreaRows}
            onChange={onChange}
            pt={{
              root: {
                className: twMerge(
                  'shadow-none rounded-none w-full resize-none outline-none text-sm text-brand-ai-1100 placeholder:text-pv-text-secondary-text bg-transparent',
                  twJoin(className?.input?.root && className.input.root)
                ),
              },
            }}
            {...(id && { id })}
            {...(typeof value !== 'undefined' && { value })}
            disabled={disabled}
            {...(onKeyDown && {
              onKeyDown: (e) => {
                setTimeout(() => handleRowResize(e), 0);
                onKeyDown(e);
              },
            })}
            onPaste={(e) => {
              e.stopPropagation();
              setTimeout(() => handleRowResize(e), 0);
            }}
            ref={inputRef}
            {...props}
          />
        )}
        {showClearInput && value?.length > 0 && (
          <XCircle
            size={16}
            className={twMerge(
              'text-pv-neutral-grey-600 hover:text-pv-neutral-grey-900 ml-2 cursor-pointer',
              className?.input?.clear && className.input.clear
            )}
            onClick={clearInput}
          />
        )}
        {rightElem
          ? typeof rightElem === 'function'
            ? rightElem()
            : rightElem
          : null}
      </div>
    </div>
  );
}
